import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Lock, ExternalLink, Clock, AlertTriangle } from "lucide-react";

const money = (cur, n) => `${cur === "USD" ? "$" : cur + " "}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Owner-facing NEEC POS Stripe billing: first-time secure checkout, then portal. All amounts come from the backend planner.
export default function StripeCheckoutCard() {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const loc = useLocation(); const nav = useNavigate();
  const load = () => api.get("/billing/stripe/me").then((r) => setV(r.data)).catch(() => setV({}));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const q = new URLSearchParams(loc.search).get("checkout");
    if (q === "success") { toast.success("Thank you. Your payment is being confirmed."); load(); }
    if (q === "cancelled") toast("Checkout cancelled. Nothing was charged.");
    if (q) nav("/billing", { replace: true });
  }, [loc.search]); // eslint-disable-line

  const checkout = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try { const { data } = await api.post("/billing/stripe/checkout", {}); window.location.assign(data.url); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); setBusy(false); }
  };
  const portal = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/stripe/portal", {}); window.location.assign(data.url); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (!v || !v.billing_enabled) return null;
  const p = v.pricing, pc = v.planned_checkout, cur = pc?.currency || p?.currency || "USD";
  const STATUS = { onboarding: "Awaiting Activation", awaiting_payment: v.activation_pending ? "Awaiting Activation" : "Awaiting Payment", active: "Active", past_due: "Past Due", suspended: "Suspended", cancelled: "Cancelled", trialing: "Trial" };
  const d10 = (x) => (x ? String(x).slice(0, 10) : "-");
  const hasSub = !!v.stripe_subscription_id;
  return (
    <Card className="p-6 space-y-4" data-testid="stripe-billing-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-head font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-500" /> NEEC POS Standard</p>
        <Badge className={`text-sm ${v.account_status === "active" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : v.account_status === "past_due" ? "bg-orange-500/10 text-orange-600 border-orange-500/20" : "bg-amber-500/10 text-amber-600 border-amber-500/20"}`} data-testid="stripe-lifecycle">{STATUS[v.account_status] || v.lifecycle_label || "-"}</Badge>
      </div>

      {hasSub && p && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm rounded-xl border border-border p-3" data-testid="stripe-summary">
          <span className="text-muted-foreground">Initial setup fee</span><span className="text-right font-semibold" data-testid="st-setup">{money(cur, p.initial_setup_fee)} · <span className={`capitalize ${p.initial_setup_fee_status === "paid" ? "text-emerald-600" : ""}`}>{(p.initial_setup_fee_status || "not_due").replace(/_/g, " ")}</span></span>
          <span className="text-muted-foreground">Active locations</span><span className="text-right font-semibold" data-testid="st-locations">{p.billable_location_count}</span>
          <span className="text-muted-foreground">Monthly subscription</span><span className="text-right font-semibold" data-testid="st-monthly">{money(cur, p.current_recurring_total)}/month</span>
          {v.cancel_at_period_end && v.account_status !== "cancelled"
            ? <><span className="text-muted-foreground">Access through</span><span className="text-right font-semibold" data-testid="st-access-through">{d10(v.cancellation_effective_at)}</span></>
            : <><span className="text-muted-foreground">Next payment</span><span className="text-right font-semibold" data-testid="st-next-payment">{v.account_status === "cancelled" ? "None" : d10(v.next_payment_date)}</span></>}
          <span className="text-muted-foreground">Subscription</span><span className="text-right capitalize" data-testid="st-sub-status">{(v.stripe_subscription_status || "created").replace(/_/g, " ")}</span>
        </div>
      )}
      {hasSub && v.cancel_at_period_end && v.account_status !== "cancelled" && (
        <p className="text-sm text-muted-foreground rounded-xl border border-border p-3" data-testid="st-cancel-notice">Your subscription is scheduled to end on {d10(v.cancellation_effective_at)}. You will continue to have access until then.</p>
      )}

      {v.account_status === "past_due" && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-sm space-y-1" data-testid="stripe-past-due-notice">
          <p className="font-semibold text-amber-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> We could not process your last subscription payment.</p>
          {v.past_due_amount != null && <p data-testid="st-past-due-amount">Amount due: <span className="font-semibold">{money(cur, v.past_due_amount)}</span></p>}
          {v.grace_period_ends_at && <p className="text-muted-foreground text-xs" data-testid="st-grace-until">Your account stays fully active until {d10(v.grace_period_ends_at)} while the payment is retried. Please update your payment method in Manage Billing to avoid any interruption.</p>}
        </div>
      )}

      {v.account_status === "suspended" && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-sm space-y-1" data-testid="stripe-suspended-notice">
          <p className="font-semibold text-amber-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Subscription payment overdue — access temporarily limited.</p>
          {v.past_due_amount != null && <p data-testid="st-suspended-amount">Amount due: <span className="font-semibold">{money(cur, v.past_due_amount)}</span></p>}
          <p className="text-muted-foreground text-xs">Your data is retained. Use Manage Billing to update your payment method; full access returns automatically once the payment succeeds.</p>
        </div>
      )}

      {v.activation_pending && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm" data-testid="stripe-activation-pending">
          <p className="font-semibold text-emerald-700 flex items-center gap-2"><Clock className="w-4 h-4" /> Payment received. Activation required.</p>
          <p className="text-muted-foreground text-xs mt-1">NEEC is confirming your account setup. You will be notified when activation is complete.</p>
        </div>
      )}

      {v.needs_checkout && pc && (
        <>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm rounded-xl border border-border p-3" data-testid="checkout-summary">
            <span className="text-muted-foreground">Setup Fee</span><span className="text-right font-semibold" data-testid="co-setup">{pc.one_time_total > 0 ? money(cur, pc.one_time_total) : "Not due"}</span>
            <span className="text-muted-foreground">Monthly subscription</span><span className="text-right font-semibold" data-testid="co-monthly">{money(cur, p.monthly_per_location)}/month per location</span>
            <span className="text-muted-foreground">Active locations</span><span className="text-right font-semibold" data-testid="co-locations">{p.billable_location_count}</span>
            <span className="text-muted-foreground font-semibold">First Payment Due Today</span><span className="text-right font-head font-extrabold text-lg" data-testid="co-today">{money(cur, pc.first_checkout_total)}</span>
            <span className="text-muted-foreground">Future Recurring Payment</span><span className="text-right font-semibold" data-testid="co-monthly-total">{money(cur, pc.recurring_total)}/month</span>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="co-note">
            {pc.one_time_total > 0 ? `Today's ${money(cur, pc.first_checkout_total)} includes the one-time ${money(cur, pc.one_time_total)} setup fee plus your first month. ` : ""}After that, {money(cur, pc.recurring_total)} renews each month.
          </p>
          {v.checkout_resumable && (
            <p className="text-sm rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2" data-testid="co-resumable">
              <span className="font-semibold text-sky-700">Checkout pending.</span> No payment has been taken yet — your secure checkout is ready and you can continue where you left off.
            </p>
          )}
          {err && <p className="text-sm text-rose-500" data-testid="co-error">{err}</p>}
          <Button onClick={checkout} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 w-full sm:w-auto px-8" data-testid="checkout-btn">
            <Lock className="w-4 h-4 mr-2" /> {busy ? "Preparing secure checkout..." : "Continue to Secure Checkout"}
          </Button>
          <p className="text-[11px] text-muted-foreground">Payments are processed securely through our payment provider.</p>
        </>
      )}

      {v.pending_checkout && !v.needs_checkout && !v.activation_pending && (
        <p className="text-sm text-muted-foreground" data-testid="co-pending">A checkout is already in progress. If you closed the payment page, it will become available again shortly.</p>
      )}

      {v.stripe_customer_id && (
        <Button variant="outline" onClick={portal} disabled={busy} data-testid="manage-billing-btn"><ExternalLink className="w-4 h-4 mr-2" /> Manage Billing</Button>
      )}

      {hasSub && (
        <div data-testid="stripe-payments">
          <p className="text-sm font-semibold mb-2">Recent Payments</p>
          {!(v.payments || []).length && <p className="text-sm text-muted-foreground">No payments recorded yet.</p>}
          {(v.payments || []).map((pay) => (
            <div key={pay.id} className="flex items-center justify-between gap-2 text-sm border-b border-border/50 py-1.5 flex-wrap" data-testid="stripe-payment-row">
              <span>{d10(pay.paid_on)}</span><span className="mono font-semibold">{money(pay.currency || cur, pay.amount)}</span>
              <span className="text-muted-foreground text-xs">{pay.method}{pay.label ? ` · ${pay.label}` : ""}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
